import founderApi, { type ApiResponse } from "./founderApi";
import {
  mergeFounderRecentActivity,
  type FounderActivityItem,
  type FounderAuditLogEntry,
  type FounderSecurityAlertEntry,
} from "./founderActivityLabels";

export type FounderWorkspaceCounts = {
  applicationsPendingReview: number;
  applicationsAwaitingRevision: number;
  suspendedApplications: number;
  publishedApplications: number;
  paymentFailures: number;
  activePublishers: number;
  suspendedPublishers: number;
  activeSubscriptions: number;
  publicationMode: string;
  databaseStatus: string;
  paymentGatewayStatus: string;
};

export type FounderScannerHealth = {
  engine: string;
  available: boolean;
};

export type FounderAttentionItem = {
  id: string;
  label: string;
  detail: string;
  href: string;
};

export type FounderHealthStatus = "healthy" | "attention";

export type FounderPlatformHealthItem = {
  id: string;
  label: string;
  status: FounderHealthStatus;
  detail: string;
};

export type FounderHomeDashboardData = {
  counts: FounderWorkspaceCounts;
  attentionItems: FounderAttentionItem[];
  platformHealth: FounderPlatformHealthItem[];
  recentActivity: FounderActivityItem[];
};

export function buildFounderAttentionItems(
  counts: FounderWorkspaceCounts,
  unresolvedSecurityAlerts: number,
): FounderAttentionItem[] {
  const items: FounderAttentionItem[] = [];

  if (counts.applicationsPendingReview > 0) {
    items.push({
      id: "pending-review",
      label: `${counts.applicationsPendingReview} app${counts.applicationsPendingReview === 1 ? "" : "s"} waiting for review`,
      detail: "New marketplace submissions need your decision.",
      href: "/founder/hub/apps?tab=pending",
    });
  }

  if (counts.suspendedApplications > 0) {
    items.push({
      id: "suspended-apps",
      label: `${counts.suspendedApplications} suspended app${counts.suspendedApplications === 1 ? "" : "s"}`,
      detail: "Review suspended listings that may need action.",
      href: "/founder/hub/apps?tab=all",
    });
  }

  if (counts.suspendedPublishers > 0) {
    items.push({
      id: "suspended-publishers",
      label: `${counts.suspendedPublishers} suspended publisher${counts.suspendedPublishers === 1 ? "" : "s"}`,
      detail: "Publisher accounts are currently suspended.",
      href: "/founder/hub/publishers?filter=suspended",
    });
  }

  if (counts.paymentFailures > 0) {
    items.push({
      id: "payment-failures",
      label: `${counts.paymentFailures} failed payment${counts.paymentFailures === 1 ? "" : "s"}`,
      detail: "Recent payment activity needs a quick check.",
      href: "/founder/hub/memberships?paymentStatus=failed",
    });
  }

  if (unresolvedSecurityAlerts > 0) {
    items.push({
      id: "security-alerts",
      label: `${unresolvedSecurityAlerts} security alert${unresolvedSecurityAlerts === 1 ? "" : "s"}`,
      detail: "Unresolved security items need your attention.",
      href: "/founder/hub/platform?tab=security",
    });
  }

  return items;
}

export function buildFounderPlatformHealth(
  counts: FounderWorkspaceCounts,
  scanners: FounderScannerHealth[],
  unresolvedSecurityAlerts: number,
): FounderPlatformHealthItem[] {
  const platformHealthy = counts.databaseStatus === "connected";
  const paymentsHealthy =
    (counts.paymentGatewayStatus === "configured" ||
      counts.paymentGatewayStatus === "partial") &&
    counts.paymentFailures === 0;
  const configuredScanners = scanners.filter((scanner) => scanner.engine);
  const securityHealthy =
    unresolvedSecurityAlerts === 0 &&
    (configuredScanners.length === 0 ||
      configuredScanners.every((scanner) => scanner.available));

  return [
    {
      id: "platform",
      label: "Platform",
      status: platformHealthy ? "healthy" : "attention",
      detail: platformHealthy ? "Operating normally" : "Platform status needs review",
    },
    {
      id: "payments",
      label: "Payments",
      status: paymentsHealthy ? "healthy" : "attention",
      detail: paymentsHealthy
        ? counts.paymentGatewayStatus === "partial"
          ? "One payment provider configured"
          : "Payment services are configured"
        : "Recent payment issues were detected",
    },
    {
      id: "security",
      label: "Security",
      status: securityHealthy ? "healthy" : "attention",
      detail: securityHealthy
        ? "No open security alerts"
        : "Security checks or alerts need attention",
    },
  ];
}

export function formatPublicationModeLabel(mode: string): string {
  if (mode === "instant") {
    return "Instant publishing";
  }

  return "Review before publishing";
}

export async function fetchFounderHomeDashboard(): Promise<FounderHomeDashboardData> {
  const [workspaceResponse, auditResponse, alertsResponse, scannersResponse] =
    await Promise.all([
      founderApi.get<ApiResponse<{ counts: FounderWorkspaceCounts }>>("/founder/workspace"),
      founderApi.get<ApiResponse<{ logs: FounderAuditLogEntry[] }>>(
        "/founder/audit-logs",
        { params: { limit: 10 } },
      ),
      founderApi.get<ApiResponse<{ alerts: FounderSecurityAlertEntry[] }>>(
        "/founder/security/alerts",
        { params: { limit: 5, resolved: "false" } },
      ),
      founderApi.get<ApiResponse<{ scanners: FounderScannerHealth[] }>>(
        "/founder/security/scanners",
      ),
    ]);

  const counts = workspaceResponse.data.data.counts;
  const unresolvedSecurityAlerts = alertsResponse.data.data.alerts.length;

  return {
    counts,
    attentionItems: buildFounderAttentionItems(counts, unresolvedSecurityAlerts),
    platformHealth: buildFounderPlatformHealth(
      counts,
      scannersResponse.data.data.scanners,
      unresolvedSecurityAlerts,
    ),
    recentActivity: mergeFounderRecentActivity(
      auditResponse.data.data.logs,
      alertsResponse.data.data.alerts,
    ),
  };
}
